"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChimeRoom = void 0;
const rxjs_1 = require("rxjs");
const logger_1 = require("@streem/logger");
const toolbox_1 = require("@streem/toolbox");
const streem_logger_1 = require("./util/streem_logger");
const chime_logger_1 = require("./util/chime_logger");
const permission_state_1 = require("./util/permission_state");
const amazon_chime_sdk_js_1 = require("amazon-chime-sdk-js");
const data_tracks_1 = require("./tracks/data_tracks");
const video_tracks_1 = require("./tracks/video_tracks");
const audio_tracks_1 = require("./tracks/audio_tracks");
const chime_device_observer_1 = require("./observers/chime_device_observer");
const chime_meeting_observer_1 = require("./observers/chime_meeting_observer");
const chime_connection_observer_1 = require("./observers/chime_connection_observer");
const volume_indicator_callback_1 = require("./observers/volume_indicator_callback");
const low_bandwidth_manager_1 = require("./managers/low_bandwidth_manager");
const room_monitor_1 = require("./room_monitor");
const network_quality_observer_1 = require("./observers/network_quality_observer");
const remote_attendee_manager_1 = require("./managers/remote_attendee_manager");
const remote_attendee_observer_1 = require("./observers/remote_attendee_observer");
class ChimeRoom {
    constructor({ roomVideoCredentials, remoteParticipantCapabilities, isHostSession, streemRoomId, chimeLowBandwidthOptimizationEnabled, preferredVideoCodecs, }) {
        this.streemLogger = streem_logger_1.streemLogger.extend('sdk-chime-video:ChimeRoom');
        this.connectionStateSubject = new rxjs_1.BehaviorSubject('DISCONNECTED');
        this.connectionState = this.connectionStateSubject.asObservable();
        // error subjects
        this.getUserMediaVideoErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.getUserMediaVideoError = this.getUserMediaVideoErrorSubject.asObservable();
        this.getUserMediaAudioErrorSubject = new rxjs_1.BehaviorSubject(undefined);
        this.getUserMediaAudioError = this.getUserMediaAudioErrorSubject.asObservable();
        // local audio
        this.localAudioTrackSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localAudioTrack = this.localAudioTrackSubject.asObservable();
        this.localAudioLevelSubject = new rxjs_1.BehaviorSubject(0);
        this.isLocalAudioTrackEnabled = this.buildIsAudioTrackEnabledObservable(this.localAudioTrack);
        // local data
        this.localDataTrackSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localDataTrack = this.localDataTrackSubject.asObservable();
        this.localDevicesSubject = new rxjs_1.BehaviorSubject([]);
        this.localDevices = this.localDevicesSubject.asObservable();
        // local video
        this.localVideoTrackSubject = new rxjs_1.BehaviorSubject(undefined);
        this.localVideoTrack = this.localVideoTrackSubject.asObservable();
        this.isLocalVideoTrackEnabled = this.buildIsVideoTrackEnabledObservable(this.localVideoTrack);
        // remote audio
        this.remoteAudioTrackSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteAudioTrack = this.remoteAudioTrackSubject.asObservable();
        this.remoteConnectionSubject = new rxjs_1.BehaviorSubject(false);
        this.remoteConnection = this.remoteConnectionSubject.asObservable();
        this.remoteAudioLevelSubject = new rxjs_1.BehaviorSubject(0);
        // start at 1 to prevent unnecessarily showing the network quality indicator message at the beginning of the call
        this.remoteSignalStrengthSubject = new rxjs_1.BehaviorSubject(1);
        this.remoteSignalStrength = this.remoteSignalStrengthSubject.pipe((0, rxjs_1.distinctUntilChanged)());
        // remote data
        this.remoteDataTrackSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteDataTrack = this.remoteDataTrackSubject.asObservable();
        // remote video
        this.remoteVideoTrackSubject = new rxjs_1.BehaviorSubject(undefined);
        this.remoteVideoTrack = this.remoteVideoTrackSubject.asObservable();
        this.remoteVideoAvailableSubject = new rxjs_1.BehaviorSubject(false);
        // Note: The remote track is not reliably set to disabled by Chime when the remote user's camera
        //       is turned off, so videoAvailable is used.  videoAvailable is true when any remote user's
        //       video is available, which works here since there is only a single remote user.
        this.isRemoteVideoTrackEnabled = this.remoteVideoAvailableSubject.asObservable();
        this.isRemoteVideoTrackBackgrounded = this.buildIsVideoTrackBackgroundedObservable(this.remoteVideoTrack);
        // recording
        this.recordingSubject = new rxjs_1.BehaviorSubject(false);
        this.recording = this.recordingSubject.asObservable();
        // room monitor
        // Note: start at a number other than 0 to prevent unnecessarily showing the network quality indicator
        //       message at the beginning of the call
        this.localNetworkQualitySubject = new rxjs_1.BehaviorSubject(3);
        this.localNetworkQuality = this.localNetworkQualitySubject.asObservable();
        this.remoteNetworkQualitySubject = new rxjs_1.BehaviorSubject(3);
        this.remoteNetworkQuality = this.remoteNetworkQualitySubject.asObservable();
        this.isLocalVideoFrozenSubject = new rxjs_1.BehaviorSubject(false);
        this.isLocalVideoFrozen = this.isLocalVideoFrozenSubject.asObservable();
        this.trackProviderName = 'Chime';
        this.useNamedVideoTracks = false;
        this.canReceiveVideo = false;
        this.localAttendeeManagedTracks = [];
        this.mediaWebRtcStatsSubject = new rxjs_1.Subject();
        this.mediaWebRtcStats = this.mediaWebRtcStatsSubject.asObservable();
        this.onLocalNetworkQualityChange = (quality) => {
            this.localNetworkQualitySubject.next(quality);
        };
        this.onLocalAudioLevelUpdate = (audioLevel) => {
            this.localAudioLevelSubject.next(audioLevel);
        };
        this.onRemoteNetworkQualityChange = (quality) => {
            this.remoteNetworkQualitySubject.next(quality);
        };
        this.onReconnecting = () => {
            this.streemLogger.info('Reconnecting to room');
            this.connectionStateSubject.next('RECONNECTING');
        };
        this.onReconnected = () => {
            this.streemLogger.info('Reconnected to room');
            this.connectionStateSubject.next('CONNECTED');
        };
        this.onRemoteSignalStrengthUpdate = (signalStrength) => {
            this.remoteSignalStrengthSubject.next(signalStrength);
        };
        this.onRemoteAudioLevelUpdate = (audioLevel) => {
            this.remoteAudioLevelSubject.next(audioLevel);
        };
        this.onRemoteAttendeeAdded = ({ dataTrack, videoTrack }) => {
            this.remoteDataTrackSubject.next(dataTrack);
            this.remoteVideoTrackSubject.next(videoTrack);
            this.remoteConnectionSubject.next(true);
        };
        this.onRemoteAttendeeRemoved = () => {
            this.remoteDataTrackSubject.next(undefined);
            this.remoteVideoTrackSubject.next(undefined);
            this.remoteConnectionSubject.next(false);
        };
        this.onRemoteVideoAvailableChange = (available) => {
            this.remoteVideoAvailableSubject.next(available);
        };
        // Note: references to Attendee.externalUserId is set to the Streem userId in the Backend.
        this.streemRoomId = streemRoomId;
        this.roomVideoCredentials = roomVideoCredentials;
        this.streemLogger.setContextValue(logger_1.contextValues.ROOM_ID, streemRoomId.toString());
        this.streemLogger.setContextValue(logger_1.contextValues.CHIME_ROOM_ID, roomVideoCredentials.Meeting.MeetingId);
        this.chimeLogger = new chime_logger_1.ChimeLogger(this.streemLogger);
        this.deviceController = new amazon_chime_sdk_js_1.DefaultDeviceController(this.chimeLogger);
        this.remoteCapabilitiesListener = remoteParticipantCapabilities.subscribe(capabilities => {
            var _a;
            this.canReceiveVideo = (_a = capabilities === null || capabilities === void 0 ? void 0 : capabilities.canReceiveVideo) !== null && _a !== void 0 ? _a : true;
        });
        this.isHostSession = isHostSession;
        this.lowBandwidthOptimizationEnabled = chimeLowBandwidthOptimizationEnabled;
        this.preferredVideoCodecs = preferredVideoCodecs;
        this.streemLogger.info('ChimeRoom created');
    }
    initializeSession() {
        const { Meeting, Attendee } = this.roomVideoCredentials;
        const configuration = new amazon_chime_sdk_js_1.MeetingSessionConfiguration(Meeting, Attendee);
        const healthPolicyConfiguration = new amazon_chime_sdk_js_1.ConnectionHealthPolicyConfiguration();
        //
        // connectionUnhealthyThreshold corresponds to the max value of consecutiveStatsWithNoPackets in
        //     ConnectionHealthData (see: https://aws.github.io/amazon-chime-sdk-js/interfaces/audiovideoobserver.html#connectionhealthdidchange)
        //     Based on observation, this value is incremented every second when there is no network connection.
        //     This means that connectionUnhealthyThreshold = X will wait roughly X seconds before
        //     reconnecting.  This corresponds (approximately) to the time it takes for
        //     the wall sync connection to reconnect.
        healthPolicyConfiguration.connectionUnhealthyThreshold = 2;
        configuration.connectionHealthPolicyConfiguration = healthPolicyConfiguration;
        this.meetingSession = new amazon_chime_sdk_js_1.DefaultMeetingSession(configuration, this.chimeLogger, this.deviceController);
    }
    registerDeviceManager(meetingSession) {
        return __awaiter(this, void 0, void 0, function* () {
            const audioInputDevices = yield this.deviceController.listAudioInputDevices();
            const audioOutputDevices = yield this.deviceController.listAudioOutputDevices();
            const videoInputDevices = yield this.deviceController.listVideoInputDevices();
            this.deviceManager = new chime_device_observer_1.ChimeDeviceObserver(devices => {
                this.streemLogger.setContextValue(logger_1.contextValues.AVAILABLE_MEDIA_DEVICES, devices);
                this.streemLogger.info('Available media devices updated', {
                    audioInputDevices: devices.filter(device => device.kind === 'audioinput'),
                    audioOutputDevices: devices.filter(device => device.kind === 'audiooutput'),
                    videoInputDevices: devices.filter(device => device.kind === 'videoinput'),
                });
                this.streemLogger.removeContextValue(logger_1.contextValues.AVAILABLE_MEDIA_DEVICES);
                this.localDevicesSubject.next(devices);
            }, audioInputDevices, audioOutputDevices, videoInputDevices);
            meetingSession.audioVideo.addDeviceChangeObserver(this.deviceManager);
        });
    }
    createMediaStream(config) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            this.initializeSession();
            (0, toolbox_1.invariant)(this.meetingSession, 'Meeting session should be defined');
            // If this is a host session we should always create a local video track. If it is a spectator session we only want to
            //       create a local track if the remote client can receive video.
            const shouldCreateLocalVideoTrack = !!((_a = config.constraints) === null || _a === void 0 ? void 0 : _a.video) && (this.isHostSession || this.canReceiveVideo);
            this.meetingSession.audioVideo.setDeviceLabelTrigger(() => {
                return navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: shouldCreateLocalVideoTrack,
                });
            });
            this.chimeConnectionObserver = new chime_connection_observer_1.ChimeConnectionObserver(this.onReconnecting);
            this.meetingSession.audioVideo.addObserver(this.chimeConnectionObserver);
            this.chimeMeetingObserver = new chime_meeting_observer_1.ChimeMeetingObserver(this.onReconnected);
            (0, toolbox_1.invariant)(this.meetingSession.eventController, 'Event controller should be defined');
            this.meetingSession.eventController.addObserver(this.chimeMeetingObserver);
            this.lowBandwidthManager = new low_bandwidth_manager_1.LowBandwidthManager({
                audioVideo: this.meetingSession.audioVideo,
                attendeeId: this.roomVideoCredentials.Attendee.AttendeeId,
                externalUserIdForLocalUser: this.roomVideoCredentials.Attendee.ExternalUserId,
                localVideoTrackEnabledObservable: this.isLocalVideoTrackEnabled,
                remoteVideoTrackEnabledObservable: this.isRemoteVideoTrackEnabled,
                chimeConnectionObserver: this.chimeConnectionObserver,
                lowBandwidthOptimizationEnabled: this.lowBandwidthOptimizationEnabled,
            });
            this.remoteAttendeeManager = new remote_attendee_manager_1.RemoteAttendeeManager(this.roomVideoCredentials.Attendee.AttendeeId, this.meetingSession.audioVideo, this.onRemoteAudioLevelUpdate, this.onRemoteSignalStrengthUpdate, this.onRemoteAttendeeAdded, this.onRemoteAttendeeRemoved);
            this.remoteAttendeeObserver = new remote_attendee_observer_1.RemoteAttendeeObserver(this.meetingSession.audioVideo, this.remoteAttendeeManager, this.onRemoteVideoAvailableChange);
            this.meetingSession.audioVideo.addObserver(this.remoteAttendeeObserver);
            try {
                yield this.registerDeviceManager(this.meetingSession);
            }
            catch (e) {
                this.streemLogger.error('Failed to register device change manager', {
                    errorMessage: e.message,
                    stack: e.stack,
                });
            }
            (0, toolbox_1.invariant)(this.deviceManager, 'Device manager should be defined');
            this.deviceManager.getUserMediaVideoError.subscribe(videoPermissionError => {
                this.getUserMediaVideoErrorSubject.next(videoPermissionError);
            });
            this.deviceManager.getUserMediaAudioError.subscribe(audioPermissionError => {
                this.getUserMediaAudioErrorSubject.next(audioPermissionError);
            });
            // Create Audio Track
            let localAudioTrackCreated = false;
            try {
                const localAudioTrack = new audio_tracks_1.ChimeLocalAudioTrack(this.meetingSession.audioVideo, this.roomVideoCredentials.Attendee.AttendeeId, this.roomVideoCredentials.Attendee.ExternalUserId, this.localAudioLevelSubject);
                localAudioTrackCreated = yield localAudioTrack.initializeAudioTrack((_b = config.constraints) === null || _b === void 0 ? void 0 : _b.audio, this.deviceManager.audioInputDevices);
                this.localAudioTrackSubject.next(localAudioTrack);
                this.localAttendeeManagedTracks.push(localAudioTrack);
            }
            catch (error) {
                // This error is thrown when the user denies permission to use the microphone
                // We get "OverconstrainedError" when we fail to startAudioInput() in initializeAudioTrack
                if (error.name === 'OverconstrainedError') {
                    this.getUserMediaAudioErrorSubject.next('NotAllowedError');
                }
                else {
                    this.getUserMediaAudioErrorSubject.next(error.name);
                }
                this.streemLogger.error(`Failed to initialize local audio track:`, {
                    errorMessage: error.message,
                    stack: error.stack,
                });
            }
            let localVideoTrackCreated = false;
            let chimeLocalVideoTrack = undefined;
            if (shouldCreateLocalVideoTrack) {
                try {
                    chimeLocalVideoTrack = new video_tracks_1.ChimeLocalVideoTrack(this.meetingSession.audioVideo, this.roomVideoCredentials.Attendee.ExternalUserId, this.preferredVideoCodecs);
                    yield chimeLocalVideoTrack.initializeVideoTrack((_c = config.constraints) === null || _c === void 0 ? void 0 : _c.video, this.deviceManager.videoInputDevices);
                    this.localVideoTrackSubject.next(chimeLocalVideoTrack);
                    this.localAttendeeManagedTracks.push(chimeLocalVideoTrack);
                    localVideoTrackCreated = true;
                }
                catch (error) {
                    // This error is thrown when the user denies permission to use the camera
                    // We get "PermissionDeniedError" when we fail to startVideoInput() in initializeVideoTrack
                    if (error.name === 'PermissionDeniedError') {
                        this.getUserMediaVideoErrorSubject.next('NotAllowedError');
                    }
                    else {
                        this.getUserMediaVideoErrorSubject.next(error.name);
                    }
                    this.streemLogger.error('Failed to initialize local video track', {
                        errorMessage: error.message,
                        stack: error.stack,
                    });
                }
            }
            let localDataTrackCreated = false;
            try {
                const localDataTrack = new data_tracks_1.ChimeLocalDataTrack(this.meetingSession.audioVideo);
                this.localDataTrackSubject.next(localDataTrack);
                this.localAttendeeManagedTracks.push(localDataTrack);
                localDataTrackCreated = true;
            }
            catch (error) {
                this.streemLogger.error('Failed to initialize local data track', {
                    errorMessage: error.message,
                    stack: error.stack,
                });
            }
            // create the volumeIndicatorCallback for the local audio track
            this.localAudioVolumeIndicatorCallback = (0, volume_indicator_callback_1.createVolumeIndicatorCallback)(this.roomVideoCredentials.Attendee.AttendeeId, this.onLocalAudioLevelUpdate);
            this.meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(this.roomVideoCredentials.Attendee.AttendeeId, this.localAudioVolumeIndicatorCallback);
            // There is only one remote audio track for the meeting, so we do not need to wait for a remote
            //   attendee to join the room
            let remoteAudioTrackCreated = false;
            try {
                const remoteAudioTrack = new audio_tracks_1.ChimeRemoteAudioTrack(this.meetingSession.audioVideo, this.roomVideoCredentials.Attendee.AttendeeId, this.roomVideoCredentials.Attendee.ExternalUserId, this.remoteAudioLevelSubject);
                yield remoteAudioTrack.initializeAudioTrack(this.deviceManager.audioOutputDevices, config.preferredAudioOutputDeviceId);
                this.remoteAudioTrackSubject.next(remoteAudioTrack);
                // associate the remote audio track with the local attendeeId
                this.localAttendeeManagedTracks.push(remoteAudioTrack);
                remoteAudioTrackCreated = true;
            }
            catch (error) {
                this.streemLogger.error('Failed to initialize remote audio track', {
                    errorMessage: error.message,
                    stack: error.stack,
                });
            }
            this.meetingSession.audioVideo.start();
            // Enables camera on in lobby for CV.
            // Enables camera on in EV2. This was previously handled in the EV2 app.
            if (chimeLocalVideoTrack) {
                chimeLocalVideoTrack.setEnabled((_e = (_d = config.video) === null || _d === void 0 ? void 0 : _d.active) !== null && _e !== void 0 ? _e : false);
            }
            return {
                audio: localAudioTrackCreated && remoteAudioTrackCreated,
                video: localVideoTrackCreated,
                data: localDataTrackCreated,
            };
        });
    }
    connect(roomId, _) {
        return __awaiter(this, void 0, void 0, function* () {
            // should be tested in all 4 browsers!
            const permissionState = yield (0, permission_state_1.getPermissionsState)();
            this.streemLogger.setContextValue(logger_1.contextValues.PERMISSIONS_STATE, permissionState);
            (0, toolbox_1.invariant)(this.chimeMeetingObserver, 'ChimeMeetingObserver should be defined');
            yield this.chimeMeetingObserver.waitForMeetingStart();
            this.connectionStateSubject.next('CONNECTED');
            this.streemLogger.info('Connected to room: ', roomId.toString());
            (0, toolbox_1.invariant)(this.chimeConnectionObserver, 'ChimeConnectionObserver should be defined');
            this.roomMonitor = new room_monitor_1.RoomMonitor(this.roomVideoCredentials.Attendee.AttendeeId, this.connectionState, this.isLocalAudioTrackEnabled, this.isLocalVideoTrackEnabled, this.chimeConnectionObserver);
            this.roomWebRtcStatsAndNetworkQualitySubscription = (0, rxjs_1.interval)(5000)
                .pipe((0, rxjs_1.withLatestFrom)(this.roomMonitor.roomWebRtcStats))
                .subscribe(([_, webRtcStats]) => {
                this.logStats(webRtcStats);
            });
            this.networkQualityObserver = new network_quality_observer_1.NetworkQualityObserver({
                roomWebRtcStats: this.roomMonitor.roomWebRtcStats,
                isLocalVideoTrackEnabled: this.isLocalVideoTrackEnabled,
                isLocalAudioTrackEnabled: this.isLocalAudioTrackEnabled,
                isRemoteVideoTrackEnabled: this.isRemoteVideoTrackEnabled,
                isRemoteVideoTrackBackgrounded: this.isRemoteVideoTrackBackgrounded,
                remoteSignalStrength: this.remoteSignalStrength,
                onLocalNetworkQualityChange: this.onLocalNetworkQualityChange,
                onRemoteNetworkQualityChange: this.onRemoteNetworkQualityChange,
            });
        });
    }
    disconnect() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        return __awaiter(this, void 0, void 0, function* () {
            // disconnect cleans up all resources (like a destructor), not only resources created by connect()
            (_a = this.remoteCapabilitiesListener) === null || _a === void 0 ? void 0 : _a.unsubscribe();
            (_b = this.roomWebRtcStatsAndNetworkQualitySubscription) === null || _b === void 0 ? void 0 : _b.unsubscribe();
            (0, toolbox_1.invariant)(this.meetingSession, 'Meeting session should be defined');
            yield Promise.allSettled(this.localAttendeeManagedTracks.map(track => track.disconnect()));
            (_c = this.meetingSession) === null || _c === void 0 ? void 0 : _c.audioVideo.realtimeUnsubscribeFromVolumeIndicator(this.roomVideoCredentials.Attendee.AttendeeId, this.localAudioVolumeIndicatorCallback);
            (_d = this.remoteAttendeeObserver) === null || _d === void 0 ? void 0 : _d.disconnect();
            (_e = this.remoteAttendeeManager) === null || _e === void 0 ? void 0 : _e.disconnect();
            // @ts-expect-error TS2345: this.remoteAttendeeObserver should be defined here
            this.meetingSession.audioVideo.removeObserver(this.remoteAttendeeObserver);
            (_f = this.networkQualityObserver) === null || _f === void 0 ? void 0 : _f.disconnect();
            (_g = this.roomMonitor) === null || _g === void 0 ? void 0 : _g.disconnect();
            // It is expected that these properties are defined when disconnect is called, so ignoring the
            //     TS errors rather than adding checks for each one
            // @ts-expect-error TS2345: this.deviceManager should be defined here
            this.meetingSession.audioVideo.removeDeviceChangeObserver(this.deviceManager);
            // @ts-expect-error TS2345: this.chimeConnectionObserver should be defined here
            this.meetingSession.audioVideo.removeObserver(this.chimeConnectionObserver);
            this.meetingSession.audioVideo.stop();
            yield ((_h = this.chimeMeetingObserver) === null || _h === void 0 ? void 0 : _h.waitForMeetingStop());
            this.connectionStateSubject.next('DISCONNECTED');
            // @ts-expect-error TS2345: this.chimeMeetingObserver should be defined here
            (_j = this.meetingSession.eventController) === null || _j === void 0 ? void 0 : _j.removeObserver(this.chimeMeetingObserver);
            (_k = this.lowBandwidthManager) === null || _k === void 0 ? void 0 : _k.disconnect();
            yield this.deviceController.destroy();
            yield ((_l = this.meetingSession) === null || _l === void 0 ? void 0 : _l.destroy());
            this.resetState();
            this.streemLogger.removeContextValue(logger_1.contextValues.PERMISSIONS_STATE);
            this.streemLogger.removeContextValue(logger_1.contextValues.ROOM_ID);
            this.streemLogger.removeContextValue(logger_1.contextValues.CHIME_ROOM_ID);
            this.streemLogger.info(`The room has been disconnected, roomId=${(_m = this.streemRoomId) === null || _m === void 0 ? void 0 : _m.toString()}`);
        });
    }
    resetState() {
        this.remoteConnectionSubject.next(false);
        this.localAudioTrackSubject.next(undefined);
        this.localVideoTrackSubject.next(undefined);
        this.localDataTrackSubject.next(undefined);
        this.remoteAudioTrackSubject.next(undefined);
        this.remoteVideoTrackSubject.next(undefined);
        this.remoteDataTrackSubject.next(undefined);
        this.localNetworkQualitySubject.next(0);
        this.remoteConnectionSubject.next(false);
        this.getUserMediaVideoErrorSubject.next(undefined);
        this.getUserMediaAudioErrorSubject.next(undefined);
        this.localAttendeeManagedTracks = [];
        this.meetingSession = undefined;
        this.deviceManager = undefined;
        this.chimeConnectionObserver = undefined;
        this.chimeMeetingObserver = undefined;
        this.lowBandwidthManager = undefined;
        this.remoteAttendeeManager = undefined;
        this.remoteAttendeeObserver = undefined;
        this.roomMonitor = undefined;
        this.networkQualityObserver = undefined;
    }
    buildIsVideoTrackEnabledObservable(videoTrack) {
        return videoTrack.pipe((0, rxjs_1.switchMap)(track => (track ? track.enabled : (0, rxjs_1.of)(false))), (0, rxjs_1.shareReplay)(1));
    }
    buildIsAudioTrackEnabledObservable(audioTrack) {
        return audioTrack.pipe((0, rxjs_1.switchMap)(track => (track ? track.enabled : (0, rxjs_1.of)(false))), (0, rxjs_1.shareReplay)(1));
    }
    buildIsVideoTrackBackgroundedObservable(videoTrack) {
        return videoTrack.pipe((0, rxjs_1.switchMap)(track => (track && track.backgrounded ? track.backgrounded : (0, rxjs_1.of)(false))), (0, rxjs_1.shareReplay)(1));
    }
    logStats(statsReport) {
        return __awaiter(this, void 0, void 0, function* () {
            const { localVideoTrackStats, localAudioTrackStats, remoteVideoTrackStats, remoteAudioTrackStats, } = statsReport;
            const compositeMediaStats = {
                localVideo: localVideoTrackStats[0]
                    ? Object.assign({}, localVideoTrackStats[0]) : {},
                localAudio: localAudioTrackStats[0]
                    ? Object.assign({}, localAudioTrackStats[0]) : {},
                remoteVideo: remoteVideoTrackStats[0]
                    ? Object.assign({}, remoteVideoTrackStats[0]) : {},
                remoteAudio: remoteAudioTrackStats[0]
                    ? Object.assign({}, remoteAudioTrackStats[0]) : {},
            };
            this.streemLogger.setContextValue(logger_1.contextValues.CHIME_WEB_RTC_STATS, compositeMediaStats);
            this.streemLogger.info('Chime WebRTC stats');
            this.streemLogger.removeContextValue(logger_1.contextValues.CHIME_WEB_RTC_STATS);
        });
    }
}
exports.ChimeRoom = ChimeRoom;
//# sourceMappingURL=chime_room.js.map